import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Plus, Bot, Phone, Settings, Trash2, Edit,
  Loader2, Search, MoreVertical
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const PROVIDER_LABELS = {
  'openai': 'OpenAI Realtime',
  'deepgram': 'Deepgram',
  'custom': 'Custom Voice',
  'intent-ivr': 'Intent IVR'
};

const PROVIDER_COLORS = {
  'openai': 'bg-green-100 text-green-800',
  'deepgram': 'bg-blue-100 text-blue-800',
  'custom': 'bg-purple-100 text-purple-800',
  'intent-ivr': 'bg-amber-100 text-amber-800'
};

// Get the correct voice based on provider
const getAgentVoice = (agent) => {
  switch (agent.provider) {
    case 'openai':
      return agent.voice || 'shimmer';
    case 'deepgram':
      return agent.deepgram_voice || 'aura-asteria-en';
    case 'custom':
    case 'intent-ivr':
      return agent.custom_voice || 'Rachel';
    default:
      return agent.voice || 'Default';
  }
};

const Agents = () => {
  const { user: currentUser } = useAuth();
  const isReadOnly = currentUser?.role === 'viewer';
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    loadAgents();
  }, []);

  const loadAgents = async () => {
    try {
      const response = await api.getAgents();
      setAgents(response.data.agents || []);
    } catch (error) {
      toast.error('Failed to load agents');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete agent "${name}"? This cannot be undone.`)) return;
    
    setDeleting(id);
    try {
      await api.deleteAgent(id);
      toast.success('Agent deleted');
      loadAgents();
    } catch (error) {
      toast.error('Failed to delete agent');
    } finally {
      setDeleting(null);
    }
  };

  const filteredAgents = agents.filter(agent =>
    agent.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Voice Agents</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your AI voice agents
          </p>
        </div>
        {!isReadOnly && (
          <Link
            to="/agents/new"
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-5 h-5 mr-2" />
            Create Agent
          </Link>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          placeholder="Search agents..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>

      {/* Agent List */}
      {filteredAgents.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <Bot className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No agents found</h3>
          <p className="text-gray-500 mb-4">Create your first voice agent to get started</p>
          {!isReadOnly && (
            <Link
              to="/agents/new"
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              <Plus className="w-5 h-5 mr-2" />
              Create Agent
            </Link>
          )}
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Agent
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Provider
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredAgents.map((agent) => (
                <tr key={agent.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-primary-100 flex items-center justify-center">
                        <Bot className="w-5 h-5 text-primary-600" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {agent.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {getAgentVoice(agent)}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      PROVIDER_COLORS[agent.provider] || 'bg-gray-100 text-gray-800'
                    }`}>
                      {PROVIDER_LABELS[agent.provider] || agent.provider}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                    {agent.type}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      agent.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {agent.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      <Link
                        to={`/agents/${agent.id}`}
                        className="p-2 text-gray-400 hover:text-primary-600 rounded-lg hover:bg-gray-100"
                        title={isReadOnly ? 'View' : 'Edit'}
                      >
                        <Edit className="w-4 h-4" />
                      </Link>
                      {agent.provider === 'intent-ivr' && (
                        <Link
                          to={`/agents/${agent.id}/ivr`}
                          className="p-2 text-gray-400 hover:text-amber-600 rounded-lg hover:bg-gray-100"
                          title="IVR Config"
                        >
                          <Settings className="w-4 h-4" />
                        </Link>
                      )}
                      {!isReadOnly && (
                        <button
                          onClick={() => handleDelete(agent.id, agent.name)}
                          disabled={deleting === agent.id}
                          className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-gray-100 disabled:opacity-50"
                          title="Delete"
                        >
                          {deleting === agent.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Agents;
